import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Check, Sun, BadgeCheck, ShieldCheck, Frame, ThermometerSun,
  Award, Eye, Star, Cog, CloudRain, Lock, Ruler,
} from "lucide-react";
import { MaskedLines, Reveal, SectionHeading } from "./Reveal";
import { EnquiryForm } from "./EnquiryForm";
import { BrochureDialog } from "./BrochureDialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../ui/accordion";

const NAVY = "#24333F";
const DARK_TXT = "rgba(30,42,54,0.85)";
const ICONS = { Check, Sun, BadgeCheck, ShieldCheck, Frame, ThermometerSun, Award, Eye, Star, Cog, CloudRain, Lock, Ruler };

const isDarkHex = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return 0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) < 110;
};

const Brochure = ({ theme }) => (
  <BrochureDialog label={theme.brochureLabel} brand={theme.brand} accent={theme.accent} accentText={theme.accentText} />
);

export const Hero = ({ m, theme }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 130]);
  return (
    <section className="relative min-h-[92vh] overflow-hidden flex items-center pt-32 pb-16">
      <motion.div style={{ y }} className="absolute inset-0">
        {m.image ? <img src={m.image} alt={m.alt || m.lines.join(" ")} className="w-full h-[120%] object-cover" /> : <div className="w-full h-[120%] bg-[#0B162C]" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050A15]/85 via-[#050A15]/55 to-[#050A15]/70" />
      </motion.div>
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <Reveal delay={0.1}>
            <p className="overline-x mb-5">
              {m.breadcrumb && (
                <>
                  <Link to={m.breadcrumb.to} className="hover:text-white transition-colors">{m.breadcrumb.label}</Link>
                  <span className="mx-2">/</span>
                </>
              )}
              {m.kicker}
            </p>
          </Reveal>
          <MaskedLines as="h1" lines={m.lines} delay={0.2}
            className="font-display tracking-tighter text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95]" />
          <Reveal delay={0.5} className="mt-6">
            <h2 className="text-base md:text-lg text-white/75 max-w-md font-normal">{m.subtitle}</h2>
          </Reveal>
          {m.brochure && (
            <Reveal delay={0.65} className="mt-9">
              <Brochure theme={theme} />
            </Reveal>
          )}
          {m.buttons && (
            <Reveal delay={0.75} className="mt-5 flex flex-wrap gap-4">
              {m.buttons.map((b) =>
                b.to ? (
                  <Link key={b.label} to={b.to} className={`btn-${b.style}`}>{b.label}</Link>
                ) : (
                  <a key={b.label} href={b.href} className={`btn-${b.style}`}>{b.label}</a>
                )
              )}
            </Reveal>
          )}
        </div>
        <Reveal delay={0.4}>
          <div data-testid="consultation-card" className="bg-[#0B162C]/85 backdrop-blur-xl border border-white/15 p-8 lg:p-10 max-w-md lg:ml-auto w-full">
            <p className="overline-x mb-2" style={{ color: isDarkHex(theme.accent) ? "rgba(255,255,255,0.9)" : theme.accent }}>Book a consultation</p>
            <p className="text-sm text-white/55 mb-6">Free, no-obligation advice from our team.</p>
            <EnquiryForm defaultProduct={m.consultProduct} />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const About = ({ m }) => (
  <section className="bg-[#F4F4F2]">
    <div className={`max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 gap-14 ${m.sideImage ? "lg:grid-cols-2" : "lg:grid-cols-12"}`}>
      <div className={m.sideImage ? "" : "lg:col-span-4"}>
        <SectionHeading number={m.number} kicker={m.kicker} title={m.heading} light />
        {m.sideImage && <AboutBody m={m} />}
      </div>
      {m.sideImage ? (
        <Reveal delay={0.15} className="img-hover min-h-[420px]">
          <img src={m.sideImage} alt={m.alt || m.heading} loading="lazy" className="w-full h-full object-cover" />
        </Reveal>
      ) : (
        <div className="lg:col-span-7 lg:col-start-6">
          <AboutBody m={m} />
        </div>
      )}
    </div>
  </section>
);

const AboutBody = ({ m }) => (
  <>
    <div className="space-y-5 -mt-4">
      {m.paragraphs.map((p, i) => (
        <Reveal key={i} delay={i * 0.05}>
          <p className="leading-relaxed" style={{ color: DARK_TXT }}>{p}</p>
        </Reveal>
      ))}
    </div>
    {m.badges && (
      <Reveal className="flex flex-wrap items-end gap-8 mt-10">
        {m.badges.map((b) => (
          <img key={b} src={b} alt="Accreditation" loading="lazy" className="h-16 lg:h-20 w-auto object-contain" />
        ))}
      </Reveal>
    )}
    {m.quickEnquiry && (
      <Reveal className="mt-10">
        <a href="#enquiry" className="btn-green">Quick Enquiry</a>
      </Reveal>
    )}
  </>
);

export const RangeCards = ({ m, theme }) => {
  const txt = m.bg ? "#ffffff" : theme.accentText;
  const n = m.cards.length;
  const cols = n === 3 || n === 5 ? "lg:grid-cols-3" : n > 3 ? "lg:grid-cols-4" : "";
  return (
  <section data-testid="range-section" style={{ backgroundColor: m.bg || theme.accent }}>
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <Reveal className="text-center">
        <p className="overline-x" style={{ color: txt, opacity: 0.6 }}>{m.number} · Explore the range</p>
        <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4" style={{ color: txt }}>
          {m.heading}
        </h2>
      </Reveal>
      <div className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-10 lg:gap-12 mt-14`}>
        {m.cards.map((r, i) => {
          const inner = (
            <>
              <div className="img-hover aspect-[16/11] shadow-2xl">
                <img src={r.img} alt={r.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <h3 className="font-display uppercase tracking-tight text-2xl mt-8 text-center" style={{ color: txt }}>{r.title}</h3>
              {r.copy && <p className="text-center leading-relaxed mt-4 max-w-lg mx-auto" style={{ color: txt, opacity: 0.8 }}>{r.copy}</p>}
              {r.to && (
                <div className="text-center mt-7">
                  <span className="btn-dark">View Range <ArrowRight size={14} /></span>
                </div>
              )}
            </>
          );
          return (
            <Reveal key={r.title} delay={i * 0.1}>
              {r.to ? (
                <Link to={r.to} data-testid={`range-card-${i}`} className="group block">{inner}</Link>
              ) : (
                <div data-testid={`range-card-${i}`} className="group">{inner}</div>
              )}
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
  );
};

export const FullImage = ({ m }) => (
  <section className="h-[55vh] overflow-hidden">
    <img src={m.image} alt={m.alt || "Installation detail"} loading="lazy" className="w-full h-full object-cover" />
  </section>
);

export const CaseStudy = ({ m, theme }) => {
  const hasSide = m.logo || !m.hideBrochure || m.link;
  return (
  <section data-testid="casestudy-section" style={{ backgroundColor: m.bg || NAVY }}>
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <Reveal className="text-center max-w-4xl mx-auto">
        <p className="overline-x">{m.number} · {m.kicker || "Real project"}</p>
        <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl text-white mt-4">{m.heading}</h2>
        {m.text && <p className="text-white/65 leading-relaxed mt-6">{m.text}</p>}
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-14 items-center">
        <Reveal className={hasSide ? "lg:col-span-2" : "lg:col-span-3 w-full"}>
          <div className="aspect-video w-full shadow-2xl">
            <iframe src={m.video} title={m.heading} className="w-full h-full" loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        </Reveal>
        {hasSide && (
        <Reveal delay={0.15} className="text-center">
          {m.logo && <img src={m.logo} alt={`${theme.brand} logo`} loading="lazy" className="w-52 mx-auto rounded-sm" />}
          <div className="mt-8 flex flex-col items-center gap-4">
            {!m.hideBrochure && <Brochure theme={theme} />}
            {m.link && (
              <a href={m.link.href} target="_blank" rel="noopener noreferrer" data-testid="casestudy-external-link"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#1e2a36] text-white text-xs font-semibold tracking-[0.18em] uppercase hover:bg-[#0f1720] transition-colors">
                {m.link.label} <ArrowRight size={14} />
              </a>
            )}
          </div>
        </Reveal>
        )}
      </div>
    </div>
  </section>
  );
};

export const Handles = ({ m }) => (
  <section data-testid="handles-section" className="bg-[#F4F4F2]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center">
      <Reveal>
        <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl" style={{ color: NAVY }}>{m.heading}</h2>
        {m.sub && <p className="mt-4" style={{ color: DARK_TXT }}>{m.sub}</p>}
      </Reveal>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16 mt-16">
        {m.items.map((h, i) => (
          <Reveal key={h.label} delay={(i % 3) * 0.08}>
            <img src={h.img} alt={h.label} loading="lazy" className="h-40 w-auto mx-auto object-contain" />
            <p className="font-semibold mt-6" style={{ color: NAVY }}>{h.label}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const FeatureSplit = ({ m }) => (
  <section className="bg-[#F4F4F2]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <Reveal className={`img-hover aspect-[16/11] ${m.reverse ? "lg:order-2" : ""}`}>
        <img src={m.image} alt={m.alt || m.heading} loading="lazy" className="w-full h-full object-cover" />
      </Reveal>
      <Reveal delay={0.1} className={m.reverse ? "lg:order-1" : ""}>
        <h2 className="font-display uppercase tracking-tight text-2xl sm:text-3xl" style={{ color: NAVY }}>{m.heading}</h2>
        {m.paragraphs.map((p, i) => (
          <p key={i} className="leading-relaxed mt-5" style={{ color: DARK_TXT }}>{p}</p>
        ))}
      </Reveal>
    </div>
  </section>
);

export const Benefits = ({ m }) => {
  const light = m.variant === "light";
  const wide = m.items.some((i) => i.text);
  const cols = m.items.length > 4 ? "lg:grid-cols-3" : "lg:grid-cols-4";
  return (
    <section data-testid="benefits-section" style={{ backgroundColor: light ? "#F4F4F2" : NAVY }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center">
        <Reveal>
          <p className="overline-x" style={light ? { color: "rgba(30,42,54,0.55)" } : {}}>{m.number} · Key benefits</p>
          <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl mt-4" style={{ color: light ? NAVY : "#fff" }}>
            {m.heading}
          </h2>
        </Reveal>
        <div className={`grid grid-cols-2 ${cols} ${wide ? "text-left gap-x-10 gap-y-14" : "gap-10"} mt-16`}>
          {m.items.map((b, i) => {
            const Icon = ICONS[b.icon] || Check;
            return (
              <Reveal key={b.title} delay={(i % 4) * 0.08} className={wide ? "" : "text-center"}>
                {b.img ? (
                  <img src={b.img} alt="" loading="lazy" className={`h-14 w-14 object-contain ${wide ? "" : "mx-auto"}`} />
                ) : (
                  <Icon size={46} strokeWidth={1.5} className={wide ? "" : "mx-auto"} style={{ color: light ? NAVY : "rgba(255,255,255,0.85)" }} />
                )}
                <p className={`font-semibold mt-5 ${wide ? "" : "max-w-[230px] mx-auto"}`} style={{ color: light ? NAVY : "rgba(255,255,255,0.92)" }}>
                  {b.title}
                </p>
                {b.text && (
                  <p className={`text-sm leading-relaxed mt-3 ${light ? "" : "text-white/60"}`} style={light ? { color: "rgba(30,42,54,0.75)" } : {}}>
                    {b.text}
                  </p>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const BrochureBanner = ({ m, theme }) => (
  <section className="relative overflow-hidden">
    <img src={m.image} alt={m.alt || "Installation"} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <Reveal>
        <div className="max-w-xl p-10 lg:p-14" style={{ backgroundColor: "rgba(36,51,63,0.94)" }}>
          <h2 className="font-display uppercase tracking-tight text-3xl text-white">Download a brochure</h2>
          <p className="text-white/70 leading-relaxed mt-5">{m.text}</p>
          <div className="mt-8">
            <Brochure theme={theme} />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Gallery = ({ m }) => (m.images || []).length === 0 ? null : (
  <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28" data-testid="gallery-section">
    <SectionHeading number={m.number} kicker="In situ" title={m.heading || "Gallery."} />
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {m.images.map((src, i) => (
        <Reveal key={src} delay={(i % 4) * 0.06} className={i % 7 === 0 ? "col-span-2 row-span-2" : ""}>
          <div className="img-hover h-full">
            <img src={src} alt={`Installation ${i + 1}`} loading="lazy" className="w-full h-full object-cover aspect-square" />
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

// self-classifying image: small graphics/logos render compact, photos render large
const SmartImg = ({ src, alt, tile }) => {
  const [kind, setKind] = useState("photo");
  const onLoad = (e) => {
    const img = e.target;
    const ar = img.naturalWidth / img.naturalHeight;
    setKind(img.naturalWidth < 420 || ar > 2.2 || ar < 0.3 ? "graphic" : "photo");
  };
  if (kind === "graphic") return <img src={src} alt={alt} loading="lazy" onLoad={onLoad} className="h-12 md:h-16 w-auto object-contain self-center" />;
  return (
    <img src={src} alt={alt} loading="lazy" onLoad={onLoad}
      className={tile ? "w-full h-full object-cover aspect-square" : "w-full h-auto max-h-[560px] object-cover"} />
  );
};

// free-form content section: heading + paragraphs + bullets + images + video (migrated static page copy)
export const RichText = ({ m }) => {
  const variant = m.variant || "light";
  const light = variant === "light";
  const bg = light ? "bg-[#F4F4F2] border-t border-black/5" : variant === "navy" ? "" : "bg-[#070E1D]";
  const txt = light ? DARK_TXT : "rgba(255,255,255,0.65)";
  const head = light ? NAVY : "#fff";
  const imgs = (m.images || []).filter(Boolean);
  const paras = (m.paragraphs || []).filter(Boolean);
  const bullets = (m.bullets || []).filter(Boolean);
  const split = imgs.length > 0 && imgs.length <= 2 && paras.length > 0;
  const Copy = () => (
    <div className="space-y-5 max-w-3xl">
      {paras.map((p, i) => <p key={i} className="leading-relaxed text-base md:text-lg" style={{ color: txt }}>{p}</p>)}
      {bullets.length > 0 && (
        <ul className="space-y-3 pt-2">
          {bullets.map((b, i) => (
            <li key={i} className="leading-relaxed pl-5 relative" style={{ color: txt }}>
              <span className="absolute left-0" style={{ color: light ? "#31465A" : "#5B7F9E" }}>—</span>{b}
            </li>
          ))}
        </ul>
      )}
      {m.button?.label && (
        m.button.to?.startsWith("http")
          ? <a href={m.button.to} target="_blank" rel="noopener noreferrer" className={light ? "btn-dark mt-4" : "btn-solid mt-4"}>{m.button.label} <ArrowRight size={14} /></a>
          : <Link to={m.button.to || "/contact/"} className={light ? "btn-dark mt-4" : "btn-solid mt-4"}>{m.button.label} <ArrowRight size={14} /></Link>
      )}
    </div>
  );
  return (
    <section className={bg} style={variant === "navy" ? { backgroundColor: NAVY } : undefined} data-testid="richtext-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        {(m.heading || m.kicker) && (
          <Reveal>
            {m.kicker && <p className="overline-x mb-4" style={light ? { color: "rgba(30,42,54,0.55)" } : {}}>{m.number ? `${m.number} · ` : ""}{m.kicker}</p>}
            {m.heading && <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl mb-10" style={{ color: head }}>{m.heading}</h2>}
          </Reveal>
        )}
        {split ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <Reveal><Copy /></Reveal>
            <div className="space-y-6">
              {imgs.map((src, i) => <Reveal key={`${src}-${i}`} delay={0.1} className="img-hover"><SmartImg src={src} alt={m.heading || "Cedar Views"} /></Reveal>)}
            </div>
          </div>
        ) : (
          <>
            {(paras.length > 0 || bullets.length > 0 || m.button?.label) && <Reveal><Copy /></Reveal>}
            {imgs.length > 0 && (
              <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${paras.length ? "mt-10" : ""}`}>
                {imgs.map((src, i) => (
                  <Reveal key={`${src}-${i}`} delay={(i % 4) * 0.05} className={`img-hover ${i % 7 === 0 && imgs.length > 4 ? "col-span-2 row-span-2" : ""}`}>
                    <SmartImg src={src} alt={`${m.heading || "Cedar Views"} ${i + 1}`} tile />
                  </Reveal>
                ))}
              </div>
            )}
          </>
        )}
        {m.video && (
          <Reveal className="aspect-video w-full max-w-4xl mt-10 shadow-2xl">
            <iframe src={m.video} title={m.heading || "Cedar Views video"} className="w-full h-full" loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </Reveal>
        )}
      </div>
    </section>
  );
};

export const Related = ({ m }) => (
  <section className="border-t hairline">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 flex flex-wrap items-center gap-x-10 gap-y-4">
      <p className="overline-x">Continue exploring</p>
      {m.links.map((r) => (
        <Link key={r.to} to={r.to} className="group flex items-center gap-2 font-display text-lg text-white/70 hover:text-white transition-colors">
          {r.label} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      ))}
    </div>
  </section>
);

export const TextBand = ({ m }) => {
  const light = m.variant !== "navy";
  return (
    <section className={light ? "bg-[#F4F4F2]" : ""} style={light ? {} : { backgroundColor: NAVY }}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center">
        <Reveal>
          <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl" style={{ color: light ? NAVY : "#fff" }}>{m.heading}</h2>
          {m.paragraphs.map((p, i) => (
            <p key={i} className={`leading-relaxed mt-6 max-w-4xl mx-auto ${light ? "" : "text-white/65"}`} style={light ? { color: DARK_TXT } : {}}>{p}</p>
          ))}
        </Reveal>
      </div>
    </section>
  );
};

// light band: image one side, logo + copy + external CTA the other (e.g. door designer)
export const SplitCta = ({ m, theme }) => (
  <section data-testid="splitcta-section" className="bg-white border-y border-black/5">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      <Reveal className={m.reverse ? "lg:order-2" : ""}>
        <img src={m.image} alt={m.alt || m.heading} loading="lazy" className="w-full h-auto" />
      </Reveal>
      <Reveal delay={0.1} className={`text-center lg:text-left ${m.reverse ? "lg:order-1" : ""}`}>
        {m.logo && <img src={m.logo} alt={`${theme.brand} logo`} loading="lazy" className="h-10 lg:h-12 w-auto mx-auto lg:mx-0" />}
        <p className="overline-x mt-8" style={{ color: "rgba(30,42,54,0.55)" }}>{m.number} · {m.kicker}</p>
        <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl mt-4" style={{ color: NAVY }}>{m.heading}</h2>
        <p className="leading-relaxed mt-6 max-w-lg mx-auto lg:mx-0" style={{ color: DARK_TXT }}>{m.text}</p>
        {m.button && (
          <a href={m.button.href} target="_blank" rel="noopener noreferrer" data-testid="splitcta-button"
            className="inline-flex items-center gap-2 px-8 py-4 mt-9 text-xs font-semibold tracking-[0.18em] uppercase transition-opacity hover:opacity-90"
            style={{ backgroundColor: theme.accent, color: theme.accentText }}>
            {m.button.label} <ArrowRight size={14} />
          </a>
        )}
      </Reveal>
    </div>
  </section>
);

// large full-bleed brand panels with centred logo + click-through (e.g. Residence 7 / 9)
export const BrandPanels = ({ m }) => (
  <section data-testid="brandpanels-section" className="bg-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      {m.heading && (
        <Reveal className="text-center">
          <p className="overline-x" style={{ color: "rgba(30,42,54,0.55)" }}>{m.number} · The ranges</p>
          <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4" style={{ color: NAVY }}>{m.heading}</h2>
        </Reveal>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 mt-14">
        {m.panels.map((p, i) => (
          <Reveal key={p.to} delay={i * 0.1}>
            <Link to={p.to} data-testid={`brand-panel-${i}`}
              className="group flex flex-col items-center justify-center aspect-[16/9] px-8 shadow-xl"
              style={{ backgroundColor: p.bg }}>
              <img src={p.logo} alt={p.alt} loading="lazy"
                className="h-20 lg:h-28 w-auto object-contain transition-transform duration-500 group-hover:scale-105" />
              <span className="mt-8 text-[11px] font-semibold tracking-[0.25em] uppercase text-white/75 group-hover:text-white transition-colors inline-flex items-center gap-2">
                Click to view range <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// asymmetric bento grid of linked product tiles
export const Bento = ({ m }) => (
  <section data-testid="bento-section" className="bg-white">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <Reveal className="text-center">
        <p className="overline-x" style={{ color: "rgba(30,42,54,0.55)" }}>{m.number} · {m.kicker || "Explore the range"}</p>
        <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-4" style={{ color: NAVY }}>{m.heading}</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 mt-14">
        {m.items.map((it, i) => {
          const Wrap = it.to ? Link : "div";
          const wrapProps = it.to ? { to: it.to } : {};
          return (
          <Reveal key={it.title} delay={i * 0.07} className={it.span || "lg:col-span-4"}>
            <Wrap {...wrapProps} data-testid={`bento-tile-${i}`} className={`group relative block overflow-hidden ${it.h || "min-h-[300px]"} h-full`} style={it.bg ? { backgroundColor: it.bg } : undefined}>
              {it.img && <img src={it.img} alt={it.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
              {it.img && <div className="absolute inset-0 bg-gradient-to-t from-[#050A15]/90 via-[#050A15]/25 to-transparent" />}
              <div className={`relative z-10 flex flex-col justify-end ${it.h || "min-h-[300px]"} h-full p-7`}>
                {it.tag && <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-white/50">{it.tag}</p>}
                <h3 className="font-display font-medium text-2xl text-white mt-2">{it.title}</h3>
                {it.d && <p className="text-sm text-white/65 mt-2 max-w-md">{it.d}</p>}
                {it.to ? (
                  <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 group-hover:text-white transition-colors">
                    Explore <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                ) : it.badge ? (
                  <span className="mt-4 w-fit border border-white/25 px-3 py-1.5 text-[10px] font-semibold tracking-[0.22em] uppercase text-white/70">{it.badge}</span>
                ) : null}
              </div>
            </Wrap>
          </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

// light band: grid of hand-drawn icon cards that click through to service pages
export const IconCards = ({ m }) => (
  <section data-testid="iconcards-section" className="bg-[#F4F4F2]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
      <Reveal className="text-center">
        <p className="overline-x" style={{ color: "rgba(30,42,54,0.55)" }}>{m.number} · {m.kicker}</p>
        <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl mt-4" style={{ color: NAVY }}>{m.heading}</h2>
        {m.text && <p className="leading-relaxed mt-5 max-w-2xl mx-auto" style={{ color: DARK_TXT }}>{m.text}</p>}
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
        {m.items.map((it, i) => (
          <Reveal key={it.title} delay={i * 0.08} className="h-full">
            <Link to={it.to} data-testid={`iconcard-${i}`} className="group flex flex-col bg-white border border-black/5 shadow-sm hover:shadow-xl transition-shadow h-full">
              <div className="px-8 pt-10">
                <img src={it.icon} alt={it.title} loading="lazy" className="w-full max-w-[220px] mx-auto transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="px-8 pb-10 pt-2 text-center flex flex-col flex-1">
                <h3 className="font-display uppercase tracking-tight text-xl" style={{ color: NAVY }}>{it.title}</h3>
                {it.d && <p className="text-sm leading-relaxed mt-3" style={{ color: DARK_TXT }}>{it.d}</p>}
                <span className="mt-auto pt-5 inline-flex items-center justify-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: NAVY }}>
                  Find out more <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const FaqJsonLd = ({ faqs }) => (
  <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  }) }} />
);

const Faq = ({ m }) => {
  const items = (m.items || []).filter((f) => f.q);
  const light = m.variant === "light";
  return (
    <section className={light ? "bg-white border-t hairline" : "bg-[#070E1D] border-t hairline"} data-testid="module-faq">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <SectionHeading number={m.number} kicker={m.kicker || "Common questions"} title={m.heading || "Frequently asked questions."} light={light} />
        <Accordion type="single" collapsible className="w-full -mt-6" data-testid="faq-accordion">
          {items.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className={light ? "border-[#31465A]/15" : "border-white/12"}>
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-base lg:text-lg hover:no-underline py-6"
                style={{ color: light ? NAVY : "#fff" }}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed pb-6" style={{ color: light ? "#3a4a58" : "rgba(255,255,255,0.6)" }}>
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        {items.length > 0 && <FaqJsonLd faqs={items} />}
      </div>
    </section>
  );
};

export const MODULES = {
  hero: Hero,
  about: About,
  rangeCards: RangeCards,
  fullImage: FullImage,
  caseStudy: CaseStudy,
  handles: Handles,
  featureSplit: FeatureSplit,
  benefits: Benefits,
  brochureBanner: BrochureBanner,
  gallery: Gallery,
  related: Related,
  textBand: TextBand,
  splitCta: SplitCta,
  brandPanels: BrandPanels,
  bento: Bento,
  iconCards: IconCards,
  richText: RichText,
  faq: Faq,
};
